import { useState, useEffect, useContext } from "react";
import axios from "axios";
import Preloader from "../../../shared/PreLoaders";
import { Authorization } from "../../../Context/Authorization";
import { AuthContext } from "../../../Context/AuthContext";

const inputStyle = {
  width: "100%", padding: "9px 12px", border: "1.5px solid #E2E8F0",
  borderRadius: 8, fontSize: 13, color: "#1A202C", background: "#fff",
  outline: "none", cursor: "pointer",
};
const labelStyle = { fontSize: 12, fontWeight: 600, color: "#2D3748", marginBottom: 6, display: "flex", alignItems: "center", gap: 5 };

const DetailCell = ({ label, value }) => (
  <div style={{ background: "#F7FAFC", borderRadius: 8, padding: "10px 14px", marginBottom: 10 }}>
    <div style={{ fontSize: 11, fontWeight: 600, color: "#A0AEC0", textTransform: "uppercase", letterSpacing: "0.5px", marginBottom: 3 }}>{label}</div>
    <div style={{ fontSize: 13, fontWeight: 600, color: "#1A202C" }}>{value || "—"}</div>
  </div>
);

const VFCProfile = () => {
  const { selectedJfmcType } = useContext(AuthContext);
  const [vfcData, setVfcData]               = useState([]);
  const [ranges, setRanges]                 = useState([]);
  const [divisions, setDivisions]           = useState([]);
  const [circles, setCircles]               = useState([]);
  const [selectedRange, setSelectedRange]   = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [selectedCircle, setSelectedCircle] = useState("");
  const [selectedVfc, setSelectedVfc]       = useState("");
  const [vfcProfileData, setVfcProfileData] = useState(null);
  const [loading, setLoading]               = useState(true);
  const [showProfile, setShowProfile]       = useState(false);
  const [error, setError]                   = useState("");
  const Api = process.env.REACT_APP_API;

  useEffect(() => {
    const fetchCircle = async () => {
      setLoading(true); setError("");
      try {
        const config = Authorization();
        const res = await axios.get(`${Api}/api/circles`, config);
        setCircles(res.data);
      } catch (err) { setError("Failed to fetch Circles. Please try again."); console.error(err); }
      finally { setLoading(false); }
    };
    fetchCircle();
  }, [Api]);

  useEffect(() => {
    if (!selectedCircle) return;
    const fetchDivision = async () => {
      setLoading(true); setError("");
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/divisions/divisions-by-circle`, { circleId: selectedCircle }, config);
        setDivisions(res.data);
      } catch (err) { setError("Failed to fetch Divisions."); console.error(err); }
      finally { setLoading(false); }
    };
    fetchDivision();
  }, [selectedCircle, Api]);

  useEffect(() => {
    if (!selectedDivision) return;
    const fetchRange = async () => {
      setLoading(true); setError("");
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/ranges/ranges-by-division`, { divisionId: selectedDivision }, config);
        setRanges(res.data);
      } catch (err) { setError("Failed to fetch Ranges."); console.error(err); }
      finally { setLoading(false); }
    };
    fetchRange();
  }, [selectedDivision, Api]);

  useEffect(() => {
    if (!selectedRange) return;
    const fetchVfc = async () => {
      setLoading(true); setError("");
      try {
        const config = Authorization();
        const res = await axios.post(`${Api}/api/vfcs/vfcs-by-range`, { rangeId: selectedRange, jfmcTypeId: selectedJfmcType?._id }, config);
        setVfcData(res.data.vfcs || []);
      } catch (err) { setError("Failed to fetch VFCs."); }
      finally { setLoading(false); }
    };
    fetchVfc();
  }, [selectedRange, Api, selectedJfmcType]);

  const handleSelectVfc = async (e) => {
    setSelectedVfc(e.target.value);
    setLoading(true); setError(""); setShowProfile(false);
    try {
      const config = Authorization();
      const res = await axios.post(`${Api}/api/vfcs/getVfcProfileByVfcId`, { id: e.target.value }, config);
      setVfcProfileData(res.data.vfc || null);
      setShowProfile(true);
    } catch (err) { setError("Failed to fetch VFC profile."); console.error(err); }
    finally { setLoading(false); }
  };

  const fmtDate = (v) => v ? new Date(v).toLocaleDateString("en-IN") : "—";

  /* ════════════════════════════════════════════
     RENDER
  ════════════════════════════════════════════ */
  return (
    <div>
      {loading && <Preloader loading={loading} />}

      {/* ── Page Header ── */}
      <div style={{ marginBottom: 20 }}>
        <h4 style={{ margin: 0, fontWeight: 700, color: "#1A202C", fontSize: 18 }}>JFMC Profile</h4>
        <div style={{ fontSize: 12, color: "#718096", marginTop: 3 }}>
          <span style={{ color: "#A0AEC0" }}>JFMC</span>
          <span style={{ margin: "0 6px", color: "#CBD5E0" }}>›</span>
          <span style={{ color: "#2E7D32", fontWeight: 600 }}>JFMC Profile</span>
        </div>
      </div>

      {/* ── No JFMC type selected ── */}
      {!selectedJfmcType ? (
        <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #FED7D7", padding: "48px 24px", textAlign: "center" }}>
          <div style={{ width: 60, height: 60, background: "#FFF5F5", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 14px" }}>
            <i className="mdi mdi-tag-outline" style={{ fontSize: 28, color: "#E53E3E" }} />
          </div>
          <div style={{ fontSize: 14, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>JFMC Type not selected</div>
          <div style={{ fontSize: 12, color: "#A0AEC0" }}>Use the <strong>JFMC Type</strong> selector in the top navigation bar</div>
        </div>
      ) : (
        <>
          {/* ── Filter Card ── */}
          <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.06)", padding: "20px 24px", marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 16 }}>
              <span style={{ width: 30, height: 30, background: "linear-gradient(135deg,#1B5E20,#43A047)", borderRadius: 8, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 14 }}>
                <i className="mdi mdi-filter-variant" />
              </span>
              <span style={{ fontSize: 13, fontWeight: 700, color: "#1A202C" }}>Select JFMC</span>
              <span style={{ fontSize: 11, color: "#A0AEC0", marginLeft: 4 }}>Select circle → division → range → JFMC</span>
            </div>

            <div style={{ display: "grid", gridTemplateColumns: "repeat(4, minmax(0,1fr))", gap: 16 }}>
              {/* Circle */}
              <div>
                <label style={labelStyle}><i className="mdi mdi-circle-outline" style={{ color: "#43A047" }} /> Circle</label>
                <select value={selectedCircle} onChange={(e) => { setSelectedCircle(e.target.value); setSelectedDivision(""); setSelectedRange(""); setSelectedVfc(""); setVfcProfileData(null); setShowProfile(false); }}
                  style={inputStyle} onFocus={(e) => e.target.style.borderColor = "#4CAF50"} onBlur={(e) => e.target.style.borderColor = "#E2E8F0"}>
                  <option value="">— Select Circle —</option>
                  {circles.map((c) => <option key={c._id} value={c._id}>{c.circleName}</option>)}
                </select>
              </div>
              {/* Division */}
              <div>
                <label style={{ ...labelStyle, color: selectedCircle ? "#2D3748" : "#A0AEC0" }}><i className="mdi mdi-domain" style={{ color: selectedCircle ? "#43A047" : "#CBD5E0" }} /> Division</label>
                <select value={selectedDivision} onChange={(e) => { setSelectedDivision(e.target.value); setSelectedRange(""); setSelectedVfc(""); setVfcProfileData(null); setShowProfile(false); }}
                  disabled={!selectedCircle}
                  style={{ ...inputStyle, color: selectedCircle ? "#1A202C" : "#A0AEC0", background: selectedCircle ? "#fff" : "#F7FAFC", cursor: selectedCircle ? "pointer" : "not-allowed" }}>
                  <option value="">— Select Division —</option>
                  {divisions.map((d) => <option key={d._id} value={d._id}>{d.divisionName}</option>)}
                </select>
              </div>
              {/* Range */}
              <div>
                <label style={{ ...labelStyle, color: selectedDivision ? "#2D3748" : "#A0AEC0" }}><i className="mdi mdi-map-marker-outline" style={{ color: selectedDivision ? "#43A047" : "#CBD5E0" }} /> Range</label>
                <select value={selectedRange} onChange={(e) => { setSelectedRange(e.target.value); setSelectedVfc(""); setVfcProfileData(null); setShowProfile(false); }}
                  disabled={!selectedDivision}
                  style={{ ...inputStyle, color: selectedDivision ? "#1A202C" : "#A0AEC0", background: selectedDivision ? "#fff" : "#F7FAFC", cursor: selectedDivision ? "pointer" : "not-allowed" }}>
                  <option value="">— Select Range —</option>
                  {ranges.map((r) => <option key={r._id} value={r._id}>{r.rangeName}</option>)}
                </select>
              </div>
              {/* VFC */}
              <div>
                <label style={{ ...labelStyle, color: selectedRange ? "#2D3748" : "#A0AEC0" }}><i className="mdi mdi-home-group" style={{ color: selectedRange ? "#43A047" : "#CBD5E0" }} /> JFMC</label>
                <select value={selectedVfc} onChange={handleSelectVfc}
                  disabled={!selectedRange}
                  style={{ ...inputStyle, color: selectedRange ? "#1A202C" : "#A0AEC0", background: selectedRange ? "#fff" : "#F7FAFC", cursor: selectedRange ? "pointer" : "not-allowed" }}>
                  <option value="">— Select JFMC —</option>
                  {vfcData.map((v) => <option key={v._id} value={v._id}>{v.vfcName}</option>)}
                </select>
              </div>
            </div>

            {error && (
              <div style={{ marginTop: 12, padding: "8px 14px", background: "#FFEBEE", border: "1px solid #FFCDD2", borderRadius: 8, fontSize: 13, color: "#C62828", display: "flex", alignItems: "center", gap: 8 }}>
                <i className="mdi mdi-alert-circle-outline" /> {error}
              </div>
            )}
          </div>

          {/* ── Profile Card ── */}
          {!showProfile ? (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "60px 24px", textAlign: "center" }}>
              <div style={{ width: 64, height: 64, background: "#E8F5E9", borderRadius: "50%", display: "flex", alignItems: "center", justifyContent: "center", margin: "0 auto 16px" }}>
                <i className="mdi mdi-home-search-outline" style={{ fontSize: 30, color: "#2E7D32" }} />
              </div>
              <div style={{ fontSize: 15, fontWeight: 700, color: "#2D3748", marginBottom: 6 }}>No JFMC selected</div>
              <div style={{ fontSize: 13, color: "#A0AEC0" }}>Select circle, division, range and JFMC above to view profile</div>
            </div>
          ) : vfcProfileData ? (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", boxShadow: "0 1px 4px rgba(0,0,0,0.07)", overflow: "hidden" }}>

              {/* Card header */}
              <div style={{ display: "flex", alignItems: "center", gap: 12, padding: "14px 20px", background: "linear-gradient(135deg,#1B5E20,#2E7D32)" }}>
                <span style={{ width: 36, height: 36, background: "rgba(255,255,255,0.15)", borderRadius: 9, display: "flex", alignItems: "center", justifyContent: "center", color: "#fff", fontSize: 18 }}>
                  <i className="mdi mdi-home-analytics" />
                </span>
                <div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: "#fff" }}>{vfcProfileData.vfcName || "JFMC Profile"}</div>
                  <div style={{ fontSize: 11, color: "rgba(255,255,255,0.7)" }}>JFMC detailed profile</div>
                </div>
                <button
                  onClick={() => window.print()}
                  style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 6, padding: "7px 14px", background: "rgba(255,255,255,0.15)", border: "1px solid rgba(255,255,255,0.3)", borderRadius: 8, color: "#fff", fontSize: 12, fontWeight: 600, cursor: "pointer" }}
                >
                  <i className="mdi mdi-printer" /> Print
                </button>
              </div>

              {/* Profile details */}
              <div style={{ padding: 24 }}>
                <div style={{ display: "grid", gridTemplateColumns: "repeat(2, minmax(0,1fr))", gap: 16 }}>
                  <DetailCell label="1. Formation of JFMC" value={fmtDate(vfcProfileData.dateOfEstablishment)} />
                  <DetailCell label="2. Registration of JFMC" value={fmtDate(vfcProfileData.dateOfEstablishment)} />
                  <DetailCell label="3. Registration No" value={vfcProfileData.registrationNo} />
                  <DetailCell label="4. JFMC Account No" value={vfcProfileData.accountNumber} />
                  <DetailCell label="5. Total Family" value={vfcProfileData.totalFamily} />
                  <DetailCell label="6. Total Population" value={vfcProfileData.totalPopulation} />
                  <DetailCell label="7. VFC Family" value={vfcProfileData.vfcFamily} />
                  <DetailCell label="8. Total SHG's" value={vfcProfileData.totalSHG} />
                  <DetailCell label="9. Total SHG Members" value={vfcProfileData.totalSHGMembers} />
                  <DetailCell label="10. Project Contribution" value="6,00,500/-" />
                  <DetailCell label="11. Entry Point Activities" value="60,000/-" />
                  <DetailCell label="12. Corpus Fund" value="10,11,000" />
                  <DetailCell label="13. Total Subscription" value="35,000/-" />
                  <DetailCell label="14. Total Loan Distributed" value={vfcProfileData.totalLoanAmount ? `${vfcProfileData.totalLoanAmount}/-` : "—"} />
                  <DetailCell label="15. Loan Received" value={vfcProfileData.totalAmountPaid ? `${vfcProfileData.totalAmountPaid}/-` : "—"} />
                  <DetailCell label="16. Recovery Percentage" value={vfcProfileData.recoveryPercentage ? `${vfcProfileData.recoveryPercentage}%` : "—"} />
                  <DetailCell label={`17. Bank Balance as on ${fmtDate(vfcProfileData.auditDate)}`} value={vfcProfileData.auditBalance ? `${vfcProfileData.auditBalance}/-` : "—"} />
                </div>
              </div>
            </div>
          ) : (
            <div style={{ background: "#fff", borderRadius: 12, border: "1px solid #E2E8F0", padding: "40px 24px", textAlign: "center" }}>
              <div style={{ fontSize: 13, color: "#A0AEC0" }}>No profile data available for the selected JFMC.</div>
            </div>
          )}
        </>
      )}
    </div>
  );
};

export default VFCProfile;
